// ledgerTrackA.js — pure maps + formatting helpers for the client-readable Track-A view.
// No React, no side effects. Safe to import anywhere including test environments.

// ── Event type labels ─────────────────────────────────────────────────────────

export const EVENT_TYPE_LABELS = {
  capital_call_event_amount: "Capital Call",
  distribution_event_amount: "Distribution",
  nav: "Valuation (NAV)",
  realized_marker: "Position Closed",
  capital_called_cumulative: "Capital Called (to date)",
  distributions_cumulative: "Distributions (to date)",
  commitment: "Commitment",
};

// ── Tier → confidence descriptor ──────────────────────────────────────────────

export const TIER_CONFIDENCE = {
  book_verified: { label: "Verified", tone: "emerald" },
  computed_only: { label: "Estimated", tone: "indigo" },
  honest_null: { label: "Needs your input", tone: "amber" },
};

// ── Nav status labels ─────────────────────────────────────────────────────────

export const NAV_STATUS_LABELS = {
  lp_nav: { label: "Valued by fund", tone: "gray" },
  needs_nav: { label: "Add current NAV", tone: "amber" },
  fund_level_dropped: { label: "Valued at fund level", tone: "gray" },
};

// ── Flag hints ────────────────────────────────────────────────────────────────

export const FLAG_HINTS = {
  computed_stale_nav:
    "The valuation used is from an older statement. Add a current NAV to refresh your performance metrics.",
  needs_nav:
    "No current valuation is on file. Add the latest NAV from your most recent fund statement to see full metrics.",
  call_timing_underdetermined:
    "Exact call dates are uncertain, which may affect return-rate calculations.",
};

// ── Metric tooltips ───────────────────────────────────────────────────────────

export const METRIC_TOOLTIPS = {
  DPI: "Distributions to Paid-In — how much of your invested capital has been returned to you as cash distributions.",
  TVPI: "Total Value to Paid-In — current holding value plus all distributions, divided by total invested capital.",
  RVPI: "Residual Value to Paid-In — current holding value divided by total invested capital.",
  XIRR: "Annualized return rate (IRR), accounting for the timing of every cash flow.",
};

// ── Pure accessor helpers ─────────────────────────────────────────────────────

/** Map an event type key to a human-readable label. Falls back to the raw key if unknown. */
export function eventTypeLabel(type) {
  return EVENT_TYPE_LABELS[type] ?? (type || "—");
}

/** Return the confidence descriptor {label, tone} for a tier key. */
export function confidenceForTier(tier) {
  return TIER_CONFIDENCE[tier] ?? { label: tier ?? "Unknown", tone: "gray" };
}

/** Return the nav-status descriptor {label, tone} for a status key. */
export function navStatusLabel(status) {
  return NAV_STATUS_LABELS[status] ?? { label: status ?? "", tone: "gray" };
}

/**
 * Return a plain-language reason why a metric is null, or null if the metric has a value.
 * @param {"DPI"|"TVPI"|"RVPI"|"XIRR"} metricKey
 * @param {object} m - metrics row (fields: dpi, tvpi, rvpi, xirr, xirr_confidence, nav_status)
 */
export function metricGapReason(metricKey, m) {
  if (!m) return null;
  switch (metricKey) {
    case "XIRR":
      if (m.xirr != null) return null;
      if (m.xirr_confidence === "single_cashflow")
        return "Need at least two dated cash flows to compute a return rate.";
      if (m.nav_status === "needs_nav")
        return "Add a current NAV to calculate the return rate.";
      return "Insufficient data to compute a return rate.";
    case "TVPI":
      if (m.tvpi != null) return null;
      if (m.nav_status === "needs_nav") return "Add a current NAV to unlock this metric.";
      if (m.nav_status === "fund_level_dropped")
        return "Valuation is at fund level — LP-level figure unavailable.";
      return "Current valuation unavailable.";
    case "RVPI":
      if (m.rvpi != null) return null;
      if (m.nav_status === "needs_nav") return "Add a current NAV to unlock this metric.";
      if (m.nav_status === "fund_level_dropped")
        return "Valuation is at fund level — LP-level figure unavailable.";
      return "Current valuation unavailable.";
    case "DPI":
      if (m.dpi != null) return null;
      return "No distributions recorded yet.";
    default:
      return null;
  }
}

// ── Formatting helpers (pure, reused by components) ──────────────────────────

export function fmtDate(d) {
  return d ? String(d).slice(0, 10) : "—";
}

export function fmtPct(v) {
  if (v == null) return null;
  return (Number(v) * 100).toFixed(2) + "%";
}

export function fmtMultiple(v) {
  if (v == null) return null;
  return Number(v).toFixed(4) + "×";
}

export function fmtAmount(amount, currency) {
  if (amount == null) return "—";
  const code = String(currency || "").trim().toUpperCase();
  const safeCode = /^[A-Z]{3}$/.test(code) ? code : "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: safeCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${safeCode} ${Number(amount).toLocaleString()}`;
  }
}
